import React, { useState, useEffect, useCallback } from "react";
import { Tabs } from "antd";
import Details from "./components/Details";
import Applicant from "./components/Applicant";
import apiEndpoints from "../../../../API/apiEndpoints";
import apiCall from "../../../../API/apiService";
import ModalManager from "../../../../../components/modalManager/index";
import { useDispatch } from "react-redux";
import { setOpenModal } from "../../../../../redux/Actions/UserAction";
import TableForm from "../../../../../components/AddApplicantTable";
import { toasty } from "../../../../../Helper";
import Metrix from "./components/Metrix";

const { TabPane } = Tabs;

const JobOpeningDetails = () => {
	const resetItem = () => ({
		candidate_name: "",
		candidate_email: "",
		candidate_phone: "",
		current_location: "",
		current_ctc: "",
		expected_ctc: "",
		notice_period: "",
		education_qualification: [
			{ degree_name: "", start_date: "", end_date: "" },
		],
		skills: [],
		notable_company: [],
		experience: "",
		current_company: "",
	});
	const dispatch = useDispatch();
	const [newItem, setNewItem] = useState(resetItem());
	const [editingIndex, setEditingIndex] = useState(null);
	const [activeTab, setActiveTab] = useState("1");
	const [candidates, setCandidates] = useState([]);
	const [job_opening_id, setJob_opening_id] = useState();
	const [refreshKey, setRefreshKey] = useState(0);

	useEffect(() => {
		const urlSegments = window.location.href.split("/");
		const id = urlSegments[4];
		const storedTab = localStorage.getItem('activeTab');

		if (id) {
			fetchData(id);
			setJob_opening_id(id);
			setActiveTab(storedTab ? storedTab : "1");
		} else {
			setActiveTab("1");
		}
	}, []);

	const fetchData = async (id) => {
		try {
			// Get stored filters from localStorage
			const storedFilters = localStorage.getItem("applicantListFilters");
			const { filters = {}, isFilterApplied = false } = storedFilters ? JSON.parse(storedFilters) : {};
			
			let url = `${apiEndpoints.JOB_OPENING_APPLICANTS}/${id ?? job_opening_id}`;
			
			// Add filter parameters if filters are applied
			if (isFilterApplied) {
				const queryParams = new URLSearchParams();
				
				if (filters.status) queryParams.append('status', filters.status);
				if (filters.round_no) queryParams.append('round_no', filters.round_no);
				
				if (queryParams.toString()) {
					url = `${url}?${queryParams.toString()}`;
				}
			}
			
			const data = await apiCall("get", url);

			console.log("Data:345678io", data);
			setCandidates(Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []));
		} catch (error) {
			console.error("Error fetching data:", error);
			if (error.response && error.response.status === 404) {
				console.log("No applicants found for this job opening");
				setCandidates([]);
			} else {
				toasty("Failed to fetch applicants", "error");
				setCandidates([]);
			}
		}
	};

	const handleTabChange = (key) => {
		setActiveTab(key);
		localStorage.setItem('activeTab', key);
	};

	// Function to clear all applied filters
	const clearFilters = () => {
		localStorage.setItem("applicantListFilters", JSON.stringify({
			filters: {},
			isFilterApplied: false,
			statusFilter: [],
			roundFilter: "",
			ai_scoreFilter: "",
			experienceFilter: "",
		}));
		fetchData();
	};

	// Function to switch to the Applicants tab with filters
	const switchToApplicantsTab = (roundNumber, status) => {
		// Switch to Applicants tab
		setActiveTab("2");
		localStorage.setItem('activeTab', "2");
		
		// Set filters for the Applicants tab
		const filters = {
			status: [status.toUpperCase()], // Store as array to match Applicant component format
			round_no: roundNumber
		};
		
		localStorage.setItem("applicantListFilters", JSON.stringify({
			filters,
			isFilterApplied: true,
			statusFilter: [status.toUpperCase()], // Store as array
			roundFilter: roundNumber,
			ai_scoreFilter: "",
			experienceFilter: "",
		}));
		fetchData();

		// Increment the refresh key to force a re-render
		setRefreshKey(prevKey => prevKey + 1);
	};

	const handleAddApplicant = () =>
		dispatch(setOpenModal({ modalVisible: true }));

	const handleSaveOrUpdate = async (method) => {
		try {
			await apiCall(method, apiEndpoints.CANDIDATE_DETAILS, {
				...newItem,
				job_opening_id,
			});
			toasty(`Applicant added successfully`);
			await fetchData();
			setNewItem(resetItem());
			dispatch(setOpenModal({ modalVisible: false }));
		} catch (error) {
			console.error(`Error posting data:`, error);
		}
	};

	const handleAddRow = () => handleSaveOrUpdate("post");

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setNewItem((prev) => ({ ...prev, [name]: value }));
	};

	const handleArrayInputChange = (index, field, value, arrayName) => {
		setNewItem((prev) => {
			const updatedArray = [...prev[arrayName]];
			updatedArray[index][field] = value;
			return { ...prev, [arrayName]: updatedArray };
		});
	};

	const handleAddEducation = () => {
		setNewItem((prev) => ({
			...prev,
			education_qualification: [
				...prev.education_qualification,
				{ degree_name: "", start_date: "", end_date: "" },
			],
		}));
	};

	const handleRemoveEducation = (index) => {
		setNewItem((prev) => ({
			...prev,
			education_qualification: prev.education_qualification.filter(
				(_, i) => i !== index
			),
		}));
	};

	const handleAddSkill = (skill) => {
		setNewItem((prev) => ({ ...prev, skills: [...prev.skills, skill] }));
	};

	const handleCandidateClose = useCallback(async (candidateId) => {
		console.log('handleCandidateClose started for candidateId:', candidateId);
		try {
			console.log('Attempting API call to close application');
			const response = await apiCall(
				"delete",
				`${apiEndpoints.DELETE_APPLICANT}/${candidateId}`
			);

			console.log('API call completed. Raw response:', response);
			console.log('Response type:', typeof response);

			// Consider any response as a success, unless it explicitly indicates failure
			if (response === undefined || response === null || response === '' || (typeof response === 'object' && Object.keys(response).length === 0)) {
				console.log('Empty response received. Treating as success.');
			} else if (response.error || response.status === 'error') {
				console.warn('Error indicator found in response:', response);
				throw new Error('API indicated an error');
			}

			// If we've reached this point, consider it a success
			setCandidates((prevCandidates) => {
				const newCandidates = prevCandidates.filter((candidate) => candidate.id !== candidateId);
				console.log('Candidates after removal:', newCandidates);
				return newCandidates;
			});
			toasty("Applicant closed successfully");

		} catch (error) {
			console.error("Error caught in handleCandidateClose:", error);
			
			if (error.response) {
				console.error("Error response data:", error.response.data);
				console.error("Error response status:", error.response.status);
				console.error("Error response headers:", error.response.headers);
			} else if (error.request) {
				console.error("Error request:", error.request);
			} else {
				console.error('Error message:', error.message);
			}
			
			toasty("Failed to close applicant", "error");
		} finally {
			console.log('handleCandidateClose completed. Final candidates state:', candidates);
		}
	}, [candidates]);

	const fetchCityName = async (locationId) => {
		try {
			const response = await apiCall("get", `${apiEndpoints.LOCATION}/${locationId}`);
			return response.name; // Assuming the response has a 'name' field for the city
		} catch (error) {
			console.error("Error fetching city name:", error);
			return ""; // Return empty string if there's an error
		}
	};

	return (
		<div key={refreshKey}>
			<ModalManager customWidth={window.innerWidth/300}>
				<TableForm
					item={newItem}
					onChange={handleInputChange}
					onArrayInputChange={handleArrayInputChange}
					handleAddEducation={handleAddEducation}
					handleRemoveEducation={handleRemoveEducation}
					onAddSkill={handleAddSkill}
					isEdit={editingIndex !== null}
					onSave={handleAddRow}
					onClear={() => setNewItem(resetItem())}
				/>
			</ModalManager>
			<Tabs activeKey={activeTab} onChange={handleTabChange} type="card">
				<TabPane tab="Details" key="1">
					<Details 
						handleAddApplicant={handleAddApplicant} 
						getJobOpeningDetails={fetchData}
						switchToApplicantsTab={switchToApplicantsTab}
					/>
				</TabPane>
				<TabPane tab="Applicant" key="2">
					<Applicant 
						candidates={candidates} 
						onCandidateClose={handleCandidateClose}
						clearFilters={clearFilters}
						
					/>
				</TabPane>
				<TabPane tab="Metrics" key="3">
				<Metrix 
						handleAddApplicant={handleAddApplicant} 
						getJobOpeningDetails={fetchData}
						switchToApplicantsTab={switchToApplicantsTab}
					/>
				</TabPane>
			</Tabs>
		</div>
	);
};

export default JobOpeningDetails;
